import { useQueries } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import type { BiddingZone } from "../../../services/client";
import { getDayAHeadPricesWithForecastOptions } from "../../../services/client/@tanstack/react-query.gen";
import { ONE_HOUR } from "../../../util/time";
import { adminAuthHeaders } from "../adminAuthHeaders";

export type PricePoint = {
  timestamp: Date;
  value: number;
};

// Each request returns a day worth of quarter-hourly samples starting at
// at_time, so the days have to line up on midnight to tile without gaps.
const eachDayStart = (start: Dayjs, end: Dayjs): Dayjs[] => {
  const days: Dayjs[] = [];
  for (
    let day = start.startOf("day");
    !day.isAfter(end, "day");
    day = day.add(1, "day")
  ) {
    days.push(day);
  }
  return days;
};

const toSortedPoints = (valueByTime: Map<number, number>): PricePoint[] =>
  [...valueByTime.entries()]
    .sort(([left], [right]) => left - right)
    .map(([time, value]) => ({ timestamp: new Date(time), value }));

export const useDayAheadPrices = (
  biddingZone: BiddingZone,
  start: Dayjs,
  end: Dayjs,
) =>
  useQueries({
    queries: eachDayStart(start, end).map((day) => ({
      ...getDayAHeadPricesWithForecastOptions({
        headers: adminAuthHeaders(),
        query: {
          at_time: day.toISOString(),
          bidding_zone: biddingZone,
        },
      }),
      retry: false,
      staleTime: ONE_HOUR,
    })),
    combine: (results) => {
      const actualByTime = new Map<number, number>();
      const forecastByTime = new Map<number, number>();
      for (const result of results) {
        for (const point of result.data?.actual ?? []) {
          actualByTime.set(new Date(point.timestamp).getTime(), point.value);
        }
        for (const point of result.data?.forecast ?? []) {
          forecastByTime.set(new Date(point.timestamp).getTime(), point.value);
        }
      }
      const actual = toSortedPoints(actualByTime);
      const forecast = toSortedPoints(forecastByTime);
      const failed = results.filter((result) => result.error);

      return {
        actual,
        forecast,
        failedDays: failed.length,
        isLoading: results.some((result) => result.isLoading),
        // Days beyond the published horizon 404, so a failed day is only worth
        // reporting as an error when it left us with nothing at all to draw.
        error:
          actual.length === 0 && forecast.length === 0
            ? (failed[0]?.error ?? null)
            : null,
      };
    },
  });
